import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export type SecurityMode = 'open' | 'managed' | 'strict';

export interface SecurityPolicy {
  id: string;
  organizationId: string;
  regionId: string | null;
  defaultPortSecurityMode: SecurityMode;
  createdAt: string;
  updatedAt: string;
}

export interface AllowedEndpoint {
  endpointId: string;
  macAddress: string;
  hostname: string | null;
  ouiVendor: string | null;
  addedAt: string;
  addedByUserId: string | null;
}

interface AllowedEndpointListResponse {
  allowedEndpoints: AllowedEndpoint[];
}

export interface PromoteResult {
  added: number;
  alreadyPresent: number;
}

// -- org-wide --

export function useOrgSecurityPolicy() {
  const contextReady = useActiveContextReady();
  return useQuery<SecurityPolicy>({
    queryKey: ['security-policy', 'org'],
    queryFn: async () => {
      const response = await axios.get<SecurityPolicy>(
        `${config.backendApi}/api/organization/security-policy`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: contextReady,
  });
}

export function useSetOrgSecurityPolicy() {
  const qc = useQueryClient();
  return useMutation<SecurityPolicy, Error, SecurityMode>({
    mutationFn: async (mode) => {
      const response = await axios.put<SecurityPolicy>(
        `${config.backendApi}/api/organization/security-policy`,
        { defaultPortSecurityMode: mode },
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['security-policy'] });
    },
  });
}

// -- region --

export function useRegionSecurityPolicy(regionId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<SecurityPolicy>({
    queryKey: ['security-policy', 'region', regionId],
    queryFn: async () => {
      if (!regionId) throw new Error('regionId required');
      const response = await axios.get<SecurityPolicy>(
        `${config.backendApi}/api/regions/${regionId}/security-policy`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!regionId && contextReady,
  });
}

export function useSetRegionSecurityPolicy(regionId: string) {
  const qc = useQueryClient();
  return useMutation<SecurityPolicy, Error, SecurityMode>({
    mutationFn: async (mode) => {
      const response = await axios.put<SecurityPolicy>(
        `${config.backendApi}/api/regions/${regionId}/security-policy`,
        { defaultPortSecurityMode: mode },
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['security-policy', 'region', regionId] });
    },
  });
}

export function useClearRegionSecurityPolicy(regionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await axios.delete(
        `${config.backendApi}/api/regions/${regionId}/security-policy`,
        { withCredentials: true }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['security-policy', 'region', regionId] });
    },
  });
}

// -- device default --

export function useSetDeviceDefaultSecurityMode(deviceId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SecurityMode | null>({
    mutationFn: async (mode) => {
      await axios.patch(
        `${config.backendApi}/api/devices/${deviceId}/default-security-mode`,
        { defaultPortSecurityMode: mode },
        { withCredentials: true }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['device', deviceId] });
    },
  });
}

// -- per-interface override --

export function useSetInterfaceSecurityMode(interfaceId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SecurityMode | null>({
    mutationFn: async (mode) => {
      await axios.patch(
        `${config.backendApi}/api/interfaces/${interfaceId}/security-mode`,
        { securityMode: mode },
        { withCredentials: true }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['effective-security-mode', interfaceId] });
      void qc.invalidateQueries({ queryKey: ['interface', interfaceId] });
    },
  });
}

export function useEffectiveSecurityMode(interfaceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<{ interfaceId: string; mode: SecurityMode }>({
    queryKey: ['effective-security-mode', interfaceId],
    queryFn: async () => {
      if (!interfaceId) throw new Error('interfaceId required');
      const response = await axios.get<{ interfaceId: string; mode: SecurityMode }>(
        `${config.backendApi}/api/interfaces/${interfaceId}/effective-security-mode`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!interfaceId && contextReady,
  });
}

// -- allowlist --

export function useAllowedEndpoints(interfaceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<AllowedEndpoint[]>({
    queryKey: ['allowed-endpoints', interfaceId],
    queryFn: async () => {
      if (!interfaceId) throw new Error('interfaceId required');
      const response = await axios.get<AllowedEndpointListResponse>(
        `${config.backendApi}/api/interfaces/${interfaceId}/allowed-endpoints`,
        { withCredentials: true }
      );
      return response.data.allowedEndpoints;
    },
    enabled: !!interfaceId && contextReady,
  });
}

export function useAddAllowedEndpoints(interfaceId: string) {
  const qc = useQueryClient();
  return useMutation<PromoteResult, Error, string[]>({
    mutationFn: async (endpointIds) => {
      const response = await axios.post<PromoteResult>(
        `${config.backendApi}/api/interfaces/${interfaceId}/allowed-endpoints`,
        { endpointIds },
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['allowed-endpoints', interfaceId] });
    },
  });
}

export function useRemoveAllowedEndpoint(interfaceId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (endpointId) => {
      await axios.delete(
        `${config.backendApi}/api/interfaces/${interfaceId}/allowed-endpoints/${endpointId}`,
        { withCredentials: true }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['allowed-endpoints', interfaceId] });
    },
  });
}

export function usePromoteCurrentEndpoints(interfaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post<PromoteResult>(
        `${config.backendApi}/api/interfaces/${interfaceId}/allowed-endpoints/promote-current`,
        {},
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['allowed-endpoints', interfaceId] });
    },
  });
}
